import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const PURPOSE_OPTIONS = ['preboarding', 'onboarding', 'alumni', 'exit_survey', 'offer_acceptance', 'applicant_portal'];
const STATE_OPTIONS = ['active', 'consumed', 'expired', 'revoked'];

function csvMultiFilter(label, options, optionLabel = (o) => o) {
  return {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(sel) => sel.map((s) => optionLabel(s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{optionLabel(o)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  };
}

const useMagicLinkTokensApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/magic-link-tokens?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const revoke = useCallback(async (id, reason) => {
    try {
      await api.post(`/v1/magic-link-tokens/${id}/revoke`, { reason: reason || 'admin_action' });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Resend returns the new plaintext token EXACTLY ONCE — caller is
  // responsible for showing it to the admin and not persisting it.
  const resend = useCallback(async (id) => {
    try {
      const res = await api.post(`/v1/magic-link-tokens/${id}/resend`);
      refresh();
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, [refresh]);

  const purposeLabel = useCallback((p) => t(`magicLinkToken.purpose.${p}`, p), [t]);
  const stateLabel = useCallback((s) => t(`magicLinkToken.state.${s}`, s), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'personName',
      label: t('magicLinkToken.personName'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (_, tableMeta) => data[tableMeta.rowIndex]?.personFullName || '-',
      },
    },
    {
      name: 'personEmail',
      label: t('magicLinkToken.personEmail'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'purpose',
      label: t('magicLinkToken.purpose.label'),
      options: {
        filter: true, sort: false,
        filterType: 'custom',
        customBodyRender: (v) => purposeLabel(v),
        filterOptions: csvMultiFilter(t('magicLinkToken.purpose.label'), PURPOSE_OPTIONS, purposeLabel),
      },
    },
    {
      name: 'state',
      label: t('magicLinkToken.state.label'),
      options: {
        filter: true, sort: false,
        filterType: 'custom',
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row?.derivedState ? stateLabel(row.derivedState) : '-';
        },
        filterOptions: csvMultiFilter(t('magicLinkToken.state.label'), STATE_OPTIONS, stateLabel),
      },
    },
    {
      name: 'tokenHashPreview',
      label: t('magicLinkToken.tokenPreview'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v ? `…${v}` : '-',
      },
    },
    numericFilterColumn('usedCount', t('magicLinkToken.usedCount'), { decimals: 0 }),
    {
      name: 'maxUses',
      label: t('magicLinkToken.maxUses'),
      options: { filter: false, sort: false },
    },
    dateFilterColumn('createDate', t('magicLinkToken.createDate'), lang, { includeTime: true }),
    dateFilterColumn('expiresAt', t('magicLinkToken.expiresAt'), lang, { includeTime: true }),
  ], [t, lang, data, purposeLabel, stateLabel]);

  return { fetching, data, pagination, columns, refresh, revoke, resend };
};

export default useMagicLinkTokensApi;
